import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { getApiBaseUrl } from '../../config/apiBase';
import { bannerImageStyle, defaultFraming, parseFraming } from './bannerFraming';

const GroupBannerEditModal = ({ groupId, onFinish, onCancel, open, imageUrl: currentImageUrl = '', framing: currentFraming }) => {
    const [framing, setFraming] = useState(defaultFraming);
    const [previewUrl, setPreviewUrl] = useState('');
    const [viewport, setViewport] = useState('desktop');
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const saving = useRef(false);
    const { getAccessTokenSilently } = useAuth0();
    useEffect(() => { if (open) { setFile(null); setError(''); setFraming(parseFraming(currentFraming)); } }, [open, groupId, currentFraming]);
    useEffect(() => {
        if (!file) { setPreviewUrl(currentImageUrl); return undefined; }
        const url = URL.createObjectURL(file); setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file, currentImageUrl]);
    const choose = (event) => {
        const selected = event.target.files?.[0];
        setFile(null); setError('');
        if (!selected) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(selected.type)) {
            setError('Choose a JPEG, PNG, or WebP image.'); return;
        }
        if (!selected.size || selected.size > 10 * 1024 * 1024) {
            setError('Choose a nonempty image no larger than 10 MB.'); return;
        }
        setFile(selected);
    };
    const save = async () => {
        if ((!file && !currentImageUrl) || saving.current) return;
        saving.current = true; setBusy(true); setError('');
        try {
            const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE } });
            let imageUrl = currentImageUrl;
            if (file) {
            const body = new FormData();
            body.append('image', file); body.append('groupId', groupId); body.append('column', 'banner');
            const response = await fetch(`${getApiBaseUrl()}/api/GroupsTbls/UploadGroupImage`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }, body,
            });
            if (!response.ok) {
                throw new Error(response.status === 403 ? 'Only an authorized group administrator can change the banner.' :
                    response.status === 400 || response.status === 413 ? 'The image could not be accepted. Try a smaller JPEG, PNG, or WebP image.' :
                        'The banner could not be saved. Please retry.');
            }
            const data = await response.json();
            imageUrl = data.imageUrl || data.ImageUrl;
            if (!imageUrl) throw new Error('The upload response was incomplete. Please reload the group to check its banner.');
            }
            const frameResponse = await fetch(`${getApiBaseUrl()}/api/GroupsTbls/${groupId}/banner-framing`, {
                method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(framing),
            });
            if (!frameResponse.ok) throw new Error('The framing could not be saved. If you uploaded an image, it was saved; retry to save its framing.');
            onFinish(`${imageUrl}${imageUrl.includes('?') ? '&' : '?'}v=${Date.now()}`, JSON.stringify(framing));
        } catch (err) {
            setError(err.message || 'The banner could not be saved. Please retry.');
        } finally { saving.current = false; setBusy(false); }
    };
    return <Modal title="Edit Banner" open={open} onCancel={busy ? undefined : onCancel}
        closable={!busy} maskClosable={!busy} keyboard={!busy} destroyOnClose width={800}
        footer={<><Button disabled={busy} onClick={onCancel}>Cancel</Button>
            <Button type="primary" disabled={!file && !currentImageUrl} loading={busy} onClick={save}>Save banner</Button></>}>
        <label htmlFor="group-banner-file">Banner image</label>
        <input id="group-banner-file" type="file" accept="image/jpeg,image/png,image/webp" onChange={choose} disabled={busy} />
        <p>Choose a wide image (JPEG, PNG, or WebP, up to 10 MB). Nothing changes until you save.</p>
        {file && <p>Selected: {file.name}</p>}
        {previewUrl && <fieldset disabled={busy} style={{ border: 0, padding: 0 }}>
            <legend>Adjust your banner</legend>
            <label>Image fitting <select value={framing.fit} onChange={e => setFraming({ ...framing, fit: e.target.value, zoom: 1 })}>
                <option value="contain">Fit entire image (logos)</option><option value="cover">Fill banner (photos)</option>
            </select></label>
            {['zoom', 'x', 'y'].map(key => <label key={key} style={{ display: 'block', marginTop: 12 }}>
                {{ zoom: 'Zoom', x: 'Horizontal position', y: 'Vertical position' }[key]}: {framing[key]}{key === 'zoom' ? '×' : '%'}
                <input aria-label={{ zoom: 'Zoom', x: 'Horizontal position', y: 'Vertical position' }[key]} type="range"
                    min={key === 'zoom' ? 0.25 : 0} max={key === 'zoom' ? 3 : 100} step={key === 'zoom' ? 0.05 : 1}
                    value={framing[key]} onChange={e => {
                        const nextValue = Number(e.target.value);
                        setFraming({
                            ...framing,
                            [key]: nextValue,
                            ...(key === 'zoom' && nextValue < 1 && framing.fit === 'cover' ? { fit: 'contain' } : {}),
                        });
                    }} style={{ width: '100%' }} />
            </label>)}
            <label>Background color <input type="color" value={framing.background} onChange={e => setFraming({ ...framing, background: e.target.value })} /></label>
            <Button onClick={() => setFraming({ ...defaultFraming })}>Reset framing</Button>
            <label>Preview <select value={viewport} onChange={e => setViewport(e.target.value)}><option value="desktop">Desktop</option><option value="mobile">Mobile</option></select></label>
            <div style={{ width: viewport === 'mobile' ? 280 : '100%', maxWidth: '100%', aspectRatio: viewport === 'mobile' ? '390 / 360' : '1280 / 320', position: 'relative', overflow: 'hidden', background: framing.background, margin: '16px auto' }}>
                <img src={previewUrl} alt="Banner framing preview" style={{ width: '100%', height: '100%', ...bannerImageStyle(framing) }} />
            </div>
            <p>Zooming below 1× automatically switches to “Fit entire image” so no part of the artwork is cut off. The live banner also has group text and its height varies with the screen size.</p>
        </fieldset>}
        {busy && <p role="status">Uploading and saving your banner…</p>}
        {error && <p role="alert">{error}</p>}
    </Modal>;
};
export default GroupBannerEditModal;
